import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { Footer } from '@/components/layout/footer'
import { OriginalBetHistory } from '@/components/original/play/OriginalBetHistory'
import { OriginalGamesCarouselSection } from '@/components/original/play/OriginalGamesCarouselSection'
import { OriginalPlayActionBar } from '@/components/original/play/OriginalPlayActionBar'
import { OriginalPlayDetails } from '@/components/original/play/OriginalPlayDetails'
import { OriginalPlayHero } from '@/components/original/play/OriginalPlayHero'
import { PlayShell } from '@/components/play/PlayShell'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { ORIGINAL_BET_HISTORY_PER_PAGE } from '@/config/original'
import { usePlayFullscreen } from '@/hooks/use-play-fullscreen'
import { findOriginalGameById, getSortedOriginalGames } from '@/lib/original-games'
import { useGetOriginalBetHistoryQuery, useGetOriginalGamesQuery } from '@/store/api/originalApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { setActiveSection } from '@/store/slices/configSlice'
import { selectPlayGame } from '@/store/slices/originalSlice'
import type { OriginalGame } from '@/types/original'

interface OriginalPlayLocationState {
	game?: OriginalGame
}

export default function OriginalPlay() {
	const dispatch = useAppDispatch()
	const { gameId } = useParams<{ gameId: string }>()
	const location = useLocation()
	const locationState = location.state as OriginalPlayLocationState | null
	const storedGame = useAppSelector(selectPlayGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)

	const [isTheaterMode, setIsTheaterMode] = useState(false)
	const [betHistoryPage, setBetHistoryPage] = useState(1)

	const gameFromState = locationState?.game ?? storedGame
	const shouldFetchGames = !gameFromState || gameFromState.id !== gameId

	const { data: originalGamesResponse, isLoading: isGamesLoading, isError: isGamesError } = useGetOriginalGamesQuery(undefined, { skip: !gameId })

	const game = useMemo(() => {
		if (gameFromState && gameFromState.id === gameId) {
			return gameFromState
		}

		return findOriginalGameById(originalGamesResponse?.data ?? [], gameId ?? '')
	}, [gameFromState, gameId, originalGamesResponse?.data])

	const { heroRef, handleFullscreen } = usePlayFullscreen()

	const carouselGames = useMemo(() => getSortedOriginalGames(originalGamesResponse?.data ?? []), [originalGamesResponse?.data])

	const {
		data: betHistoryResponse,
		isLoading: isBetHistoryLoading,
		isFetching: isBetHistoryFetching,
		isError: isBetHistoryError,
	} = useGetOriginalBetHistoryQuery(
		{
			game_id: gameId ?? '',
			page: betHistoryPage,
			per_page: ORIGINAL_BET_HISTORY_PER_PAGE,
		},
		{ skip: !gameId || !isAuthenticated },
	)

	const betHistory = betHistoryResponse?.data ?? []
	const betHistoryTotal = betHistoryResponse?.total ?? 0

	useEffect(() => {
		dispatch(setActiveSection('casino'))
	}, [dispatch])

	useEffect(() => {
		setIsTheaterMode(false)
		setBetHistoryPage(1)
	}, [gameId])

	useEffect(() => {
		if (!game?.id) {
			return
		}

		const frame = window.requestAnimationFrame(() => {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		})

		return () => window.cancelAnimationFrame(frame)
	}, [game?.id])

	if (!gameId) {
		return (
			<PlayShell>
				<p className='text-sm text-muted-foreground'>Game not found.</p>
				<Link to='/becy-originals' className='text-sm font-medium text-primary hover:underline'>
					Back to Originals
				</Link>
			</PlayShell>
		)
	}

	if (shouldFetchGames && isGamesLoading) {
		return (
			<PlayShell className='min-h-[50vh] items-center justify-center'>
				<BetsCycleLoader size={40} aria-label='Loading original game' />
			</PlayShell>
		)
	}

	if (!game || isGamesError) {
		return (
			<PlayShell>
				<p className='text-sm text-muted-foreground'>Unable to load this original game right now.</p>
				<Link to='/becy-originals' className='text-sm font-medium text-primary hover:underline'>
					Back to Originals
				</Link>
			</PlayShell>
		)
	}

	return (
		<PlayShell footer={<Footer />}>
			<OriginalPlayHero key={game.id} ref={heroRef} game={game} isTheaterMode={isTheaterMode} />

			<OriginalPlayActionBar
				gameId={game.id}
				gameName={game.game_name}
				onFullscreen={handleFullscreen}
				onTheaterToggle={() => setIsTheaterMode((current) => !current)}
				isTheaterMode={isTheaterMode}
			/>

			<OriginalPlayDetails game={game} />

			<OriginalBetHistory
				bets={betHistory}
				isLoading={isBetHistoryLoading}
				isFetching={isBetHistoryFetching}
				isError={isBetHistoryError}
				page={betHistoryPage}
				total={betHistoryTotal}
				onPageChange={setBetHistoryPage}
			/>

			<OriginalGamesCarouselSection games={carouselGames} currentGameId={game.id} isLoading={isGamesLoading} isError={isGamesError} />
		</PlayShell>
	)
}
